import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { useOrganization } from "../OrganizationContext"
import { useUser } from "../UserContext"
import { useUserRole } from "../UserRoleContext"
import {
  approveMonitoringBaseline,
  createBusinessConversion,
  createDraftBaseline,
  enqueueMonitoringRun,
  fetchMonitoringClient,
  fetchOrganizationMonitoringRole,
  transitionMonitoringIssue,
  updateMonitoringClient,
} from "../api/monitoring"
import {
  discoverMonitoringGoogleResources,
  saveMonitoringPlatformAccounts,
  startMonitoringGoogleOAuth,
} from "../api/monitoringGoogle"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"
import { RouteTabs } from "../components/ui/RouteTabs"
import DomainMeasurementDocumentation from "../components/DomainMeasurementDocumentation"

const terminalIssueStatuses = new Set(["resolved", "ignored", "false_positive"])
const conversionResourceTypes = new Set(["ga4_key_event", "google_ads_conversion_action"])

const formatDate = (value) => value ? new Date(value).toLocaleString() : "—"
const profileLabel = (value) => value === "automotive_asc" ? "Automotive + ASC" : "Custom baseline"

export default function ClientDetail() {
  const { accountId, tab: requestedTab = "overview" } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { organizations = [], selectedOrgId, isAutomotive = true } = useOrganization()
  const user = useUser()
  const globalRole = useUserRole()
  const [data, setData] = useState(null)
  const [memberRole, setMemberRole] = useState(null)
  const [status, setStatus] = useState("loading")
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")
  const [busy, setBusy] = useState("")
  const [settings, setSettings] = useState(null)
  const [discovery, setDiscovery] = useState(null)
  const [selectedResources, setSelectedResources] = useState(new Set())

  const load = useCallback(async () => {
    if (!selectedOrgId || !accountId || !user?.id) return
    setStatus("loading")
    try {
      const [detail, role] = await Promise.all([
        fetchMonitoringClient(selectedOrgId, accountId),
        fetchOrganizationMonitoringRole(selectedOrgId, user.id),
      ])
      setData(detail)
      setMemberRole(role)
      setSettings({
        monitoring_profile: detail.account.monitoring_profile,
        monitoring_timezone: detail.account.monitoring_timezone,
        monitoring_frequency: detail.account.monitoring_frequency,
        monitoring_enabled: detail.account.monitoring_enabled,
      })
      setError("")
      setStatus("ready")
    } catch (loadError) {
      setError(loadError.message || "Unable to load this client.")
      setStatus("error")
    }
  }, [accountId, selectedOrgId, user?.id])

  useEffect(() => { load() }, [load])

  const canManage = globalRole === "admin" || ["admin", "member"].includes(memberRole)
  const selectedOrg = organizations.find((organization) => organization.id === selectedOrgId)
  const monitoringAvailable = globalRole === "admin" || selectedOrg?.plan?.conversion_monitoring_enabled === true
  const tab = ["overview", "connections", "monitoring", "audits"].includes(requestedTab)
    ? requestedTab
    : "overview"
  const monitoringView = ["setup", "results", "history"].includes(searchParams.get("view"))
    ? searchParams.get("view")
    : "setup"
  const googleConnectionId = searchParams.get("google_connection")
  const measurementMappingId = searchParams.get("measurement_mapping")

  useEffect(() => {
    if (!googleConnectionId || !selectedOrgId || !accountId || !canManage) return
    let active = true
    setBusy("discover-google")
    discoverMonitoringGoogleResources(selectedOrgId, accountId, googleConnectionId)
      .then((result) => {
        if (!active) return
        setDiscovery(result)
        setSelectedResources(new Set((result.resources || []).map((_, index) => index)))
        setBusy("")
      })
      .catch((discoveryError) => {
        if (!active) return
        setError(discoveryError.message || "Unable to discover Google resources.")
        setBusy("")
      })
    return () => { active = false }
  }, [accountId, canManage, googleConnectionId, selectedOrgId])
  const approvedBaseline = data?.baselines.find((baseline) => baseline.status === "approved")
  const draftBaseline = data?.baselines.find((baseline) => baseline.status === "draft")
  const openIssues = data?.issues.filter((issue) => !terminalIssueStatuses.has(issue.status)) || []
  const platformById = useMemo(() => new Map((data?.platforms || []).map((item) => [item.id, item])), [data?.platforms])
  const mappingById = useMemo(() => new Map((data?.conversions || []).flatMap((conversion) =>
    (conversion.mappings || []).map((mapping) => [mapping.id, { ...mapping, businessName: conversion.name }]),
  )), [data?.conversions])
  const trackedResources = useMemo(() => new Set((data?.conversions || []).flatMap((conversion) =>
    (conversion.mappings || []).flatMap((mapping) => [
      mapping.external_conversion_id ? `id:${mapping.external_conversion_id}` : null,
      `name:${String(mapping.external_conversion_name).toLowerCase()}`,
    ].filter(Boolean)),
  )), [data?.conversions])
  const observedConversions = useMemo(() => {
    const values = new Map()
    for (const snapshot of data?.snapshots || []) {
      for (const observed of snapshot.snapshot?.resources || []) {
        if (!conversionResourceTypes.has(observed.resourceType)) continue
        const externalId = observed.externalResourceId || observed.state?.id || null
        const name = observed.state?.eventName || observed.state?.name || observed.canonicalKey
        const key = `${snapshot.platform_account_id}:${observed.resourceType}:${externalId || name}`
        values.set(key, {
          platformAccountId: snapshot.platform_account_id,
          resourceType: observed.resourceType,
          externalId,
          name,
          eventName: observed.state?.eventName || null,
          state: observed.state,
          tracked: (externalId && trackedResources.has(`id:${externalId}`)) ||
            trackedResources.has(`name:${String(name).toLowerCase()}`),
        })
      }
    }
    return [...values.values()]
  }, [data?.snapshots, trackedResources])

  const perform = async (key, action, successMessage) => {
    setBusy(key)
    setError("")
    setMessage("")
    try {
      await action()
      setMessage(successMessage)
      await load()
    } catch (actionError) {
      setError(actionError.message || "The action could not be completed.")
    } finally {
      setBusy("")
    }
  }

  const connectGoogle = async () => {
    setBusy("connect-google")
    setError("")
    try {
      const result = await startMonitoringGoogleOAuth(selectedOrgId, account.id)
      window.location.assign(result.authorization_url)
    } catch (connectError) {
      setError(connectError.message || "Unable to start Google authorization.")
      setBusy("")
    }
  }

  const saveDiscoveredResources = () => {
    const selected = (discovery?.resources || []).filter((_, index) => selectedResources.has(index))
    return perform(
      "save-google",
      async () => {
        await saveMonitoringPlatformAccounts(selectedOrgId, account.id, googleConnectionId, selected)
        setDiscovery(null)
        setSelectedResources(new Set())
        setSearchParams({}, { replace: true })
      },
      `${selected.length} Google resource${selected.length === 1 ? "" : "s"} connected.`,
    )
  }

  if (status === "loading" && !data) return <div className="page-container"><p>Loading client…</p></div>
  if (!data) return <div className="page-container ui-stack"><p className="ui-error">{error}</p><Button onClick={load}>Retry</Button></div>

  const account = data.account
  const accountTabs = [
    { key: "overview", label: "Overview", to: `/accounts/${account.id}/overview` },
    { key: "connections", label: "Connections", to: `/accounts/${account.id}/connections` },
    { key: "monitoring", label: "Monitoring", to: `/accounts/${account.id}/monitoring` },
    { key: "audits", label: "Audits", to: `/accounts/${account.id}/audits` },
  ]
  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl">
      <div className="ui-page-header">
        <div className="ui-stack ui-stack--xs">
          <Button as={Link} to="/dashboard" variant="ghost" size="sm">← All accounts</Button>
          <h1 className="ui-page-title">{account.name}</h1>
          <div className="ui-chip-row">
            <Badge variant="outline">{profileLabel(account.monitoring_profile)}</Badge>
            <Badge variant={account.health_status === "healthy" ? "accent" : "subtle"}>{account.health_status}</Badge>
          </div>
        </div>
        {canManage && monitoringAvailable && (
          <Button
            disabled={busy === "run" || !data.platforms.length}
            onClick={() => perform("run", () => enqueueMonitoringRun(account.id), "Monitoring run queued. The worker will pick it up shortly.")}
          >
            {busy === "run" ? "Queuing…" : "Run now"}
          </Button>
        )}
      </div>
      {error && <p className="ui-error" role="alert">{error}</p>}
      {message && <p className="ui-hint" role="status">{message}</p>}

      <RouteTabs label={`${account.name} sections`} items={accountTabs} activeKey={tab} />

      {tab === "monitoring" && (
        <RouteTabs
          label="Monitoring views"
          activeKey={monitoringView}
          items={[
            { key: "setup", label: "Setup", to: `/accounts/${account.id}/monitoring?view=setup` },
            { key: "results", label: "Results", to: `/accounts/${account.id}/monitoring?view=results` },
            { key: "history", label: "History", to: `/accounts/${account.id}/monitoring?view=history` },
          ]}
        />
      )}

      {tab === "overview" && <div className="ui-grid ui-grid--cards">
        <Card><CardHeader><CardTitle>Health</CardTitle><CardDescription>Current evidence and open issues</CardDescription></CardHeader><CardContent><strong>{account.health_status}</strong><p className="ui-text--muted">{openIssues.length} open issue{openIssues.length === 1 ? "" : "s"}</p></CardContent></Card>
        <Card><CardHeader><CardTitle>Approved baseline</CardTitle><CardDescription>Client-specific expected configuration</CardDescription></CardHeader><CardContent><strong>{approvedBaseline ? `Version ${approvedBaseline.version}` : "Not approved"}</strong><p className="ui-text--muted">{formatDate(approvedBaseline?.approved_at)}</p></CardContent></Card>
        <Card><CardHeader><CardTitle>Last successful run</CardTitle><CardDescription>Data is revised on a rolling window</CardDescription></CardHeader><CardContent><strong>{formatDate(account.last_successful_scan_at)}</strong><p className="ui-text--muted">Next: {formatDate(account.next_scan_at)}</p></CardContent></Card>
      </div>}

      {tab === "overview" && (
        <Card>
          <CardHeader><CardTitle>Next action</CardTitle><CardDescription>The most important step for this account.</CardDescription></CardHeader>
          <CardContent>
            {!monitoringAvailable ? <p>Monitoring is not available for this organization.</p>
              : !data.platforms.length ? <Button as={Link} to={`/accounts/${account.id}/connections`}>Connect a platform</Button>
                : !approvedBaseline ? <Button as={Link} to={`/accounts/${account.id}/monitoring?view=setup`}>Review and approve baseline</Button>
                  : openIssues.length ? <Button as={Link} to={`/accounts/${account.id}/monitoring?view=results`}>Review {openIssues.length} open issue{openIssues.length === 1 ? "" : "s"}</Button>
                    : <p className="ui-text--muted">No action required. Overnight monitoring is current.</p>}
          </CardContent>
        </Card>
      )}

      {tab === "monitoring" && !monitoringAvailable && (
        <p className="ui-text--muted">Conversion monitoring is not enabled for this organization.</p>
      )}

      {tab === "monitoring" && monitoringAvailable && monitoringView === "setup" && <Card>
        <CardHeader><CardTitle>Monitoring settings</CardTitle><CardDescription>Profile and tier are independent. Runs begin after 5:00 AM in the effective reporting timezone.</CardDescription></CardHeader>
        <CardContent>
          <form className="ui-grid ui-grid--form" onSubmit={(event) => {
            event.preventDefault()
            perform("settings", () => updateMonitoringClient(selectedOrgId, account.id, settings), "Monitoring settings saved.")
          }}>
            <label className="ui-field"><span className="ui-label">Profile</span><select className="ui-input" disabled={!canManage} value={settings.monitoring_profile} onChange={(event) => setSettings({ ...settings, monitoring_profile: event.target.value })}><option value="custom_baseline">Custom baseline</option><option value="automotive_asc">Automotive + ASC</option></select></label>
            <Input label="Fallback timezone" disabled={!canManage} value={settings.monitoring_timezone} onChange={(event) => setSettings({ ...settings, monitoring_timezone: event.target.value })} />
            <label className="ui-field"><span className="ui-label">Frequency</span><select className="ui-input" disabled={!canManage} value={settings.monitoring_frequency} onChange={(event) => setSettings({ ...settings, monitoring_frequency: event.target.value })}><option value="daily">Daily</option><option value="every_3_days">Every three days</option><option value="weekly">Weekly</option></select></label>
            <label className="ui-field"><span className="ui-label">Scheduled monitoring</span><span className="ui-stack ui-stack--row"><input type="checkbox" disabled={!canManage || !approvedBaseline} checked={settings.monitoring_enabled} onChange={(event) => setSettings({ ...settings, monitoring_enabled: event.target.checked })} /> Enabled</span>{!approvedBaseline && <span className="ui-hint">Approve a baseline before enabling overnight monitoring.</span>}</label>
            {canManage && <div className="ui-button-row"><Button type="submit" disabled={busy === "settings"}>{busy === "settings" ? "Saving…" : "Save settings"}</Button></div>}
          </form>
        </CardContent>
      </Card>}

      {tab === "connections" && <Card>
        <CardHeader><CardTitle>Platform connections</CardTitle><CardDescription>API reporting timezones take precedence over the client fallback.</CardDescription></CardHeader>
        <CardContent className="ui-stack ui-stack--md">
          {canManage && monitoringAvailable && <div className="ui-button-row"><Button variant="outline" disabled={busy === "connect-google"} onClick={connectGoogle}>{busy === "connect-google" ? "Opening Google…" : "Connect Google Ads, GA4 & GTM"}</Button></div>}
          {busy === "discover-google" && <p className="ui-text--muted">Discovering accessible Google resources…</p>}
          {discovery && (
            <div className="ui-stack ui-stack--md">
              <h3 className="ui-heading-sm">Choose resources to monitor</h3>
              {(discovery.errors || []).map((item) => <p className="ui-error" key={item.platform}>{item.platform}: {item.message}</p>)}
              <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Select</TableHeaderCell><TableHeaderCell>Platform</TableHeaderCell><TableHeaderCell>Resource</TableHeaderCell><TableHeaderCell>Timezone</TableHeaderCell></TableRow></TableHead><TableBody>{(discovery.resources || []).map((resource, index) => <TableRow key={`${resource.platform}:${resource.external_account_id}`}><TableCell><input type="checkbox" aria-label={`Select ${resource.display_name}`} checked={selectedResources.has(index)} onChange={() => setSelectedResources((current) => { const next = new Set(current); if (next.has(index)) next.delete(index); else next.add(index); return next })} /></TableCell><TableCell>{resource.platform}</TableCell><TableCell>{resource.display_name}</TableCell><TableCell>{resource.reporting_timezone || "Discovered on first run"}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
              <div className="ui-button-row"><Button disabled={!selectedResources.size || busy === "save-google"} onClick={saveDiscoveredResources}>{busy === "save-google" ? "Saving…" : `Monitor ${selectedResources.size} selected`}</Button><Button variant="ghost" onClick={() => { setDiscovery(null); setSearchParams({}, { replace: true }) }}>Cancel</Button></div>
            </div>
          )}
          {!data.platforms.length ? <p className="ui-text--muted">No Google Ads, GA4, or GTM resources are mapped yet.</p> : (
            <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Platform</TableHeaderCell><TableHeaderCell>Resource</TableHeaderCell><TableHeaderCell>Reporting timezone</TableHeaderCell><TableHeaderCell>Capabilities</TableHeaderCell><TableHeaderCell>Next scan</TableHeaderCell></TableRow></TableHead><TableBody>{data.platforms.map((platform) => <TableRow key={platform.id}><TableCell>{platform.platform}</TableCell><TableCell>{platform.display_name}</TableCell><TableCell>{platform.reporting_timezone || account.monitoring_timezone}</TableCell><TableCell>{platform.capabilities.join(", ")}</TableCell><TableCell>{formatDate(platform.next_scan_at)}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
          )}
          <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Resource type</TableHeaderCell><TableHeaderCell>Configured</TableHeaderCell><TableHeaderCell>Details</TableHeaderCell></TableRow></TableHead><TableBody>
            <TableRow><TableCell>Websites</TableCell><TableCell>{data.websites.length}</TableCell><TableCell>{data.websites.map((website) => website.url).join(", ") || "Not configured"}</TableCell></TableRow>
            <TableRow><TableCell>GA4 properties</TableCell><TableCell>{data.ga4Properties.length}</TableCell><TableCell>{data.ga4Properties.map((property) => property.property_name || property.property_id).join(", ") || "Not configured"}</TableCell></TableRow>
            <TableRow><TableCell>Meta Pixel</TableCell><TableCell>{data.paidMediaSetup?.meta_pixel_connection_id ? "Configured" : "Not configured"}</TableCell><TableCell>{data.paidMediaSetup?.meta_pixel_connection_id ? "Available for automotive auditing" : "—"}</TableCell></TableRow>
            <TableRow><TableCell>GTM container</TableCell><TableCell>{data.paidMediaSetup?.gtm_container_connection_id ? "Configured" : "Not configured"}</TableCell><TableCell>{data.paidMediaSetup?.ga4_measurement_id || "—"}</TableCell></TableRow>
            <TableRow><TableCell>Products</TableCell><TableCell>{data.products.length}</TableCell><TableCell>{data.products.map((product) => product.product).join(", ") || "Not assigned"}</TableCell></TableRow>
          </TableBody></Table></TableContainer>
        </CardContent>
      </Card>}

      {tab === "connections" && !isAutomotive && (
        <DomainMeasurementDocumentation
          organizationId={selectedOrgId}
          account={account}
          canManage={canManage}
          selectedMappingId={measurementMappingId}
          onSelectMapping={(mappingId) => {
            const next = new URLSearchParams(searchParams)
            if (!mappingId || mappingId === measurementMappingId) next.delete("measurement_mapping")
            else next.set("measurement_mapping", mappingId)
            setSearchParams(next, { replace: true })
          }}
        />
      )}

      {tab === "monitoring" && monitoringAvailable && monitoringView === "setup" && <Card>
        <CardHeader><CardTitle>Business conversions</CardTitle><CardDescription>Choose discovered technical conversions that represent outcomes for this client.</CardDescription></CardHeader>
        <CardContent className="ui-stack ui-stack--md">
          {!observedConversions.length ? <p className="ui-text--muted">Run configuration and conversion discovery to review technical conversions.</p> : (
            <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Platform</TableHeaderCell><TableHeaderCell>Conversion</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell align="right">Action</TableHeaderCell></TableRow></TableHead><TableBody>{observedConversions.map((observed) => <TableRow key={`${observed.platformAccountId}:${observed.resourceType}:${observed.externalId || observed.name}`}><TableCell>{platformById.get(observed.platformAccountId)?.platform}</TableCell><TableCell>{observed.name}</TableCell><TableCell>{observed.tracked ? <Badge variant="accent">Tracked</Badge> : <Badge>Discovered</Badge>}</TableCell><TableCell align="right">{canManage && !observed.tracked && <Button size="sm" variant="outline" disabled={busy === `conversion:${observed.name}`} onClick={() => perform(`conversion:${observed.name}`, () => createBusinessConversion({ organizationId: selectedOrgId, accountId: account.id, observed }), `${observed.name} is now a business conversion.`)}>Track</Button>}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
          )}
          {!!data.conversions.length && <p className="ui-text--muted">{data.conversions.length} business conversion{data.conversions.length === 1 ? "" : "s"} configured.</p>}
        </CardContent>
      </Card>}

      {tab === "monitoring" && monitoringAvailable && monitoringView === "setup" && <Card>
        <CardHeader><CardTitle>Baseline</CardTitle><CardDescription>Discovery never becomes an expectation until an analyst approves it.</CardDescription></CardHeader>
        <CardContent className="ui-stack ui-stack--md">
          <div className="ui-chip-row">{data.rulePacks.map((assignment) => <Badge key={assignment.id} variant={assignment.enabled ? "accent" : "subtle"}>{assignment.rule_pack?.display_name}</Badge>)}</div>
          <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Version</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Created</TableHeaderCell><TableHeaderCell>Approved</TableHeaderCell></TableRow></TableHead><TableBody>{data.baselines.map((baseline) => <TableRow key={baseline.id}><TableCell>{baseline.version}</TableCell><TableCell>{baseline.status}</TableCell><TableCell>{formatDate(baseline.created_at)}</TableCell><TableCell>{formatDate(baseline.approved_at)}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
          {canManage && <div className="ui-button-row"><Button variant="outline" disabled={busy === "baseline" || !data.snapshots.length || Boolean(draftBaseline)} onClick={() => perform("baseline", () => createDraftBaseline({ organizationId: selectedOrgId, accountId: account.id, snapshots: data.snapshots }), "Draft baseline created for review.")}>Create draft from discovery</Button>{draftBaseline && <Button disabled={busy === "approve"} onClick={() => perform("approve", () => approveMonitoringBaseline(draftBaseline.id), `Baseline version ${draftBaseline.version} approved.`)}>Approve version {draftBaseline.version}</Button>}</div>}
        </CardContent>
      </Card>}

      {tab === "audits" && (
        <Card>
          <CardHeader><CardTitle>Account audits</CardTitle><CardDescription>Open an existing audit with this account as the working context.</CardDescription></CardHeader>
          <CardContent><div className="ui-button-row">
            {selectedOrg?.plan?.website_audit_enabled && <Button as={Link} to={`/website-audit?account_id=${account.id}`} variant="outline">Website audit</Button>}
            {selectedOrg?.plan?.ga4_enabled !== false && <Button as={Link} to={data.ga4Properties[0] ? `/ga4-property?id=${data.ga4Properties[0].id}` : "/ga4-audit"} variant="outline">GA4 audit</Button>}
            {account.monitoring_profile === "automotive_asc" && selectedOrg?.plan?.paid_media_enabled && <Button as={Link} to={`/paid-media?account_id=${account.id}`} variant="outline">Automotive Meta &amp; GTM</Button>}
          </div></CardContent>
        </Card>
      )}

      {tab === "monitoring" && monitoringAvailable && monitoringView === "results" && <Card>
        <CardHeader><CardTitle>Issues</CardTitle><CardDescription>Connectivity failures remain separate from tracking failures.</CardDescription></CardHeader>
        <CardContent>{!data.issues.length ? <p className="ui-text--muted">No issues recorded.</p> : <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Severity</TableHeaderCell><TableHeaderCell>Issue</TableHeaderCell><TableHeaderCell>Dimension</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Last detected</TableHeaderCell><TableHeaderCell align="right">Action</TableHeaderCell></TableRow></TableHead><TableBody>{data.issues.map((issue) => <TableRow key={issue.id}><TableCell><Badge variant={issue.severity === "critical" ? "outline" : "subtle"}>{issue.severity}</Badge></TableCell><TableCell>{issue.title}</TableCell><TableCell>{issue.health_dimension}</TableCell><TableCell>{issue.status}</TableCell><TableCell>{formatDate(issue.last_detected_at)}</TableCell><TableCell align="right">{canManage && !terminalIssueStatuses.has(issue.status) && <Button size="sm" variant="outline" onClick={() => perform(`issue:${issue.id}`, () => transitionMonitoringIssue(issue.id, "resolved", "Resolved by analyst"), "Issue resolved.")}>Resolve</Button>}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}</CardContent>
      </Card>}

      {tab === "monitoring" && monitoringAvailable && monitoringView === "results" && <Card>
        <CardHeader><CardTitle>Recent activity</CardTitle><CardDescription>Daily values are re-fetched and revised for the prior 14 completed days.</CardDescription></CardHeader>
        <CardContent>{!data.activity.length ? <p className="ui-text--muted">No activity observations yet.</p> : <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Date</TableHeaderCell><TableHeaderCell>Conversion</TableHeaderCell><TableHeaderCell>Occurrence count</TableHeaderCell><TableHeaderCell>Native attribution</TableHeaderCell><TableHeaderCell>Revision</TableHeaderCell></TableRow></TableHead><TableBody>{data.activity.slice(0, 50).map((row) => <TableRow key={row.id}><TableCell>{row.activity_date}</TableCell><TableCell>{mappingById.get(row.conversion_mapping_id)?.businessName || "Conversion"}</TableCell><TableCell>{row.conversion_count}</TableCell><TableCell>{row.native_attribution_count ?? "—"}</TableCell><TableCell>{row.revision_count}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}</CardContent>
      </Card>}

      {tab === "monitoring" && monitoringAvailable && monitoringView === "history" && <Card>
        <CardHeader><CardTitle>Run history</CardTitle><CardDescription>Runs isolate platform failures so one client cannot stop another.</CardDescription></CardHeader>
        <CardContent>{!data.runs.length ? <p className="ui-text--muted">No monitoring runs yet.</p> : <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Scheduled</TableHeaderCell><TableHeaderCell>Type</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Data through</TableHeaderCell><TableHeaderCell>Timezone</TableHeaderCell></TableRow></TableHead><TableBody>{data.runs.map((run) => <TableRow key={run.id}><TableCell>{formatDate(run.scheduled_for)}</TableCell><TableCell>{run.trigger_type}</TableCell><TableCell>{run.status}</TableCell><TableCell>{run.data_through_date || "—"}</TableCell><TableCell>{run.reporting_timezone || "—"}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}</CardContent>
      </Card>}
    </div>
  )
}
